import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import moment from 'moment'
import { useForm, FormProvider } from 'react-hook-form'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import BodyText from 'LuxKit/Typography/BodyText'
import type { TripItem } from 'tripPlanner/types/tripItem'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import Group from 'components/utils/Group'
import { usePatchTripItem } from 'tripPlanner/hooks/api/tripItem'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import RHFDatePickerInput from 'tripPlanner/components/TripModal/TripItems/Common/Fields/RHFDatePickerInput'
import TimeInput from 'tripPlanner/components/TripModal/TripItems/Common/Fields/TimeInput'
import { shortTimeHourFormatAmPm } from 'lib/datetime/dateUtils'
import Sheet from 'components/utils/Sheet'

interface Props {
  item: TripItem
  previousItem: TripItem | undefined
  nextItem: TripItem | undefined
  changeItemDates: () => void
}

interface FormValues {
  startDate: string | undefined
  startTime: string | undefined
}

function OverlappingTimesModal({ item, previousItem, nextItem }: Props) {
  const intl = useIntl()
  const { closeModal } = useModalCallbacks()
  const { mutate: patchItem } = usePatchTripItem()

  const methods = useForm<FormValues>({
    defaultValues: {
      startDate: item.startDate?.format(ISO_DATE_FORMAT) || undefined,
      startTime: item.startTime?.format('HH:mm') || undefined,
    },
  })

  const { register, handleSubmit } = methods

  const overlappingItemText = useMemo(() => {
    if (previousItem && 'name' in previousItem) {
      return `This overlaps with ${previousItem.name} at ${previousItem.startTime ? shortTimeHourFormatAmPm(previousItem.startTime.toDate()) : ''}`
    }
    if (nextItem && 'name' in nextItem) {
      return `This overlaps with ${nextItem.name} at ${nextItem.startTime ? shortTimeHourFormatAmPm(nextItem.startTime.toDate()) : ''}`
    }
    return ''
  }, [previousItem, nextItem])

  const onSave = useCallback((values: FormValues) => {
    if (!values.startDate) return

    const startDateTime = moment(values.startDate)

    patchItem(
      {
        tripId: item.tripId,
        tripItemId: item.id,
        tripItem: {
          type: item.type,
          startDate: startDateTime.format(ISO_DATE_FORMAT),
          startTime: values.startTime || null,
        },
      },
      {
        onSuccess: () => {
          closeModal()
          showSnackbar(intl.formatMessage({
            id: 'K2UQZw_AuQsJMjmv2Ue5',
            defaultMessage: 'Time updated',
            description: 'Success notification displayed after updating a trip item time',
          }), 'success')
        },
      },
    )
  }, [item, patchItem, closeModal, intl])

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSave)}>
        <ModalHeader
          title={intl.formatMessage({
            id: 'xsSxgiG3ehUytIobdhGL',
            defaultMessage: 'Time conflict detected',
            description: 'Modal title when a trip item time overlaps with another item',
          })}
          onCloseButtonClick={closeModal}
        />
        <ModalBody>
          <Sheet padding="0 24 0 24">
            <VerticalSpacer gap={24}>
              <BodyText variant="large" colour="neutral-two">
                {overlappingItemText}
              </BodyText>
              <Group direction="horizontal" horizontalAlign="stretch" gap={16}>
                <RHFDatePickerInput
                  name="startDate"
                  label={intl.formatMessage({
                    id: 'MOH2fzIwBuZgjFROGaa2',
                    defaultMessage: 'Date',
                    description: 'Label for the date picker input field in the overlapping times conflict resolution modal',
                  })}
                  required
                />
                <TimeInput
                  {...register('startTime')}
                  label={intl.formatMessage({
                    id: 'O_ntllvtrkqcNI4xXcnL',
                    defaultMessage: 'Time',
                    description: 'Label for the time picker input field in the overlapping times conflict resolution modal',
                  })}
                  placeholder={intl.formatMessage({
                    id: 'Z--tTQPX6hms9teKisuc',
                    defaultMessage: 'Select time',
                    description: 'Placeholder text for the time picker input in the trip planner itinerary view',
                  })}
                  required
                />
              </Group>
            </VerticalSpacer>
          </Sheet>
        </ModalBody>
        <ModalFooter
          primaryActionProps={{
            children: intl.formatMessage({
              id: '2iJR-lEW8fS5h7l4bsRN',
              defaultMessage: 'Save',
              description: 'Action button to save the updated time for a trip item',
            }),
            type: 'submit',
          }}
        />
      </form>
    </FormProvider>
  )
}

export default OverlappingTimesModal
